/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: PhotoWithTag — 带标签 UI 的 Photo 卡片 (composition wrapper)
 *
 * 设计目标 (review #1/#2/#5 要求):
 *   - **原 Photo 共享组件不直接修改** (除最小 slot prop 外)
 *   - 标签相关 UI / state / 业务逻辑全部集中在本组件
 *   - 非目标团队 / 非全部照片页面继续用原 Photo (零回归风险)
 *
 * 实现:
 *   - Photo 加 2 个最小 prop:
 *       - renderInPhoto?: () => ReactNode  → 在 .photo 内部 (image 区域) 渲染额外内容
 *       - batchMode?: boolean              → 通用 UI 模式: checkbox 常驻 + magnifier 隐藏
 *   - PhotoWithTag 通过这 2 个 slot 注入: chip overlay + PhotoCardTagOverlay
 *   - 拦截 onPreview prop: batchMode 下点卡片切勾选 (无需 Photo 知道"批量打标"语义)
 *   - 业务逻辑 (chip 删除/替换) 走 usePhotoTagOperations hook
 *
 * 调用方:
 *   - photov2/PhotosList: 替换原 <Photo /> 用法
 *   - ai-photo-king/PhotosList: 同上
 *   - 其他场景 (选照片器/加水印/分类文件夹) 继续用原 Photo
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Popover, Tooltip } from 'antd'
import './index.scss'
import { getIn } from '../../../utils/photoUtils'
import { useAppSelector } from '../../../app/hooks'
import { usePhotoLabelEnabled } from '../../../app/hooks/usePhotoLabelEnabled'
import {
  useTeamPhotoTags,
  invalidateTeamPhotoTagsCache
} from '../../../app/hooks/useTeamPhotoTags'
import Photo from '../Photo'
import PhotoCardTagOverlay from '../PhotoCardTagOverlay'
import TagPickerPopover from '../TagPickerPopover'
import { useBatchTagMode } from '../BatchTagMode'
import { usePhotoTagOperations } from '../../photoTag/hooks/usePhotoTagOperations'
import { appendLabelsToPhotos } from '../../../slices/photosByContentSlice'
import { useDispatch } from 'react-redux'
import {
  trackPicspageClick,
  trackPicspageDialogShow,
  trackPicspageDialogClick
} from '../../photoTag/utils/track'

/** 单个 chip: 名称 + 移除 × (hover 才出) + 文本溢出 tooltip */
const EllipsisTag: React.FC<{
  name: string
  color: string
  tagId: string | number
  /** 点击 × 移除标签。不传 = 不可移除,× 不渲染。 */
  onRemove?: () => void
  /** 点击 chip 主体替换标签。不传 = 不可替换,点击仅 stopPropagation。 */
  onReplace?: () => void
  /** 操作 inflight 时 disable 按钮,避免重复点 */
  busy?: boolean
}> = ({ name, color, onRemove, onReplace, busy }) => {
  const spanRef = useRef<HTMLSpanElement>(null)
  const [isOverflow, setIsOverflow] = useState(false)

  useEffect(() => {
    const el = spanRef.current
    if (el) {
      setIsOverflow(el.scrollWidth > el.clientWidth)
    }
  }, [name])

  const themeColor = color || '#E53935'
  const tag = (
    <span
      ref={spanRef}
      className={
        'photo-tag' +
        (onReplace ? ' photo-tag--replaceable' : '') +
        (busy ? ' photo-tag--busy' : '')
      }
      style={{
        color: themeColor,
        backgroundColor: `${themeColor}1A`
      }}
      onClick={(e) => {
        e.stopPropagation()
        if (busy) return
        if (onReplace) onReplace()
      }}
      title={onReplace ? `${name} (点击替换)` : name}
    >
      <span className="photo-tag__name">{name}</span>
      {onRemove && (
        <span
          className="photo-tag__remove"
          onClick={(e) => {
            // 必须用 stopPropagation 拦住,否则会冒泡到 chip 主体的 onReplace
            e.stopPropagation()
            if (busy) return
            onRemove()
          }}
          aria-label="移除标签"
        >
          ×
        </span>
      )}
    </span>
  )

  return isOverflow ? <Tooltip title={name}>{tag}</Tooltip> : tag
}

interface PhotoWithTagProps {
  style?: object
  photo: any
  canCheck?: boolean
  checked?: boolean
  readyToCheck?: boolean
  onPreview?: () => void
  onCheck?: (photo: any) => void
  onUnCheck?: (photo: any) => void
  track?: any
  onAbnormalClick?: any
}

const PhotoWithTag: React.FC<PhotoWithTagProps> = (props) => {
  const { photo, canCheck, checked, onPreview, onCheck, onUnCheck } = props

  const dispatch = useDispatch()
  const photoLabelEnabled = usePhotoLabelEnabled()
  // POJO state — 直接访问属性,不使用 Immutable .get() / .getIn()
  const groupId = useAppSelector(
    (state: any) => (state.group?.groupId as string) || ''
  ) as string
  const myUserId = useAppSelector(
    (state: any) => (state.main?.user?.user_id as string) || ''
  ) as string
  const {
    isBatchMode: isBatchTagMode,
    fromPage: fromPageForTrack,
    // 哨兵: 只有被 BatchTagModeProvider 包裹的 PhotosList (photov2 / ai-photo-king)
    // 才启用打标 UI. 共享场景 (选照片器/加水印/分类文件夹) 默认 false.
    isProviderMounted: tagOverlayEnabled
  } = useBatchTagMode()
  // 非打标场景 (tagOverlayEnabled=false) 把 useTeamPhotoTags 短路掉
  const { tags: teamAllTags } = useTeamPhotoTags({ enabled: tagOverlayEnabled })

  const {
    localLabels,
    setLocalLabels,
    chipBusyId,
    replaceTargetLabel,
    setReplaceTargetLabel,
    handleChipRemove,
    handleChipReplaceCommit
  } = usePhotoTagOperations({
    photo,
    groupId: String(groupId || ''),
    fromPage: fromPageForTrack
  })

  const isLimitViewByPhotoCount = !!getIn(photo, ['isLimitViewByPhotoCount'])
  const photoSeqForOp = getIn(photo, ['seq']) as number

  // tag UI 总开关: 必须 Provider 已挂载 + 团队开通标签 + 该照片非空间已满状态
  const shouldRenderTagUI =
    tagOverlayEnabled && photoLabelEnabled && !isLimitViewByPhotoCount

  // 批量模式: 拦截 onPreview, 整卡片点击 = 切勾选 (而不是预览大图)。
  // Photo 不需要知道"批量打标"语义, 只看到 onPreview 被外部改了。
  const interceptedOnPreview = useMemo(() => {
    if (!isBatchTagMode || !canCheck) return onPreview
    return () => {
      const willCheck = !checked
      trackPicspageClick({
        clickItem: 'batch_toggle_check',
        fromPage: fromPageForTrack,
        bool1: willCheck
      })
      if (checked) {
        onUnCheck && onUnCheck(photo)
      } else {
        onCheck && onCheck(photo)
      }
    }
  }, [
    isBatchTagMode,
    canCheck,
    checked,
    onPreview,
    onUnCheck,
    onCheck,
    photo,
    fromPageForTrack
  ])

  // 在 Photo 的 .photo 内部 (image 区域) 渲染 chip overlay + PhotoCardTagOverlay
  // 通过 Photo 新增的 renderInPhoto slot 注入, anchor 到 .photo (position:relative) 上
  const renderInPhoto = useCallback(() => {
    if (!shouldRenderTagUI) return null
    const labels = localLabels
    const displayTags = labels.slice(0, 2)
    const remainingCount = labels.length - 2
    const canEditChips = !isBatchTagMode

    return (
      <>
        {/* PhotoCardTagOverlay: 卡片悬浮打标右下 + 按钮 (hover 出),
            点击弹 TagPickerPopover. batchMode 下自身隐藏 (disabled state). */}
        <PhotoCardTagOverlay
          photoFile={getIn(photo, ['file']) as string}
          photoSeq={getIn(photo, ['seq']) as number}
          photoLabels={localLabels}
          allTags={teamAllTags}
          groupId={String(groupId || '')}
          showOverlayChip={false}
          disabled={!teamAllTags.length || isBatchTagMode}
          onLabelsChange={(next) => {
            setLocalLabels(next)
            // 同步到 redux —— 防止虚拟列表回收后 useEffect 把 localLabels 重置成 redux 老数据
            // (本次 + 按钮打标是 exclusive 模式, 后端清旧再打新 → 用 exclusive 覆盖语义)
            if (typeof photoSeqForOp === 'number') {
              dispatch(
                appendLabelsToPhotos({
                  affectedSeqs: [photoSeqForOp],
                  newLabels: next.map((l) => ({
                    id: String(l.id),
                    name: l.name,
                    color: l.color
                  })),
                  exclusive: true
                })
              )
            }
            // 打标后失效缓存, 让 useCount 下次能拿到最新
            invalidateTeamPhotoTagsCache(String(groupId || ''), myUserId)
          }}
        />
        {/* chip overlay: 照片左上角设计稿位置 (跟 PhotoCardTagOverlay 的 + 按钮各占一角) */}
        {labels.length > 0 && (
          <div
            className="photo-tag-overlay"
            onClick={(e) => e.stopPropagation()}
          >
            {displayTags.map((tag: any, idx: number) => {
              const tid = String(tag.id ?? idx)
              const isReplacing =
                replaceTargetLabel && String(replaceTargetLabel.id) === tid
              const ellipsisChip = (
                <EllipsisTag
                  tagId={tag.id || idx}
                  name={tag.name}
                  color={tag.color}
                  onRemove={
                    canEditChips ? () => handleChipRemove(tid) : undefined
                  }
                  onReplace={
                    canEditChips
                      ? () => {
                          // 埋点: 点 chip 主体打开替换 popover
                          trackPicspageClick({
                            clickItem: 'card_chip_replace_open',
                            fromPage: fromPageForTrack,
                            string1: tid,
                            labelName: tag.name,
                            tabName: 'team_label'
                          })
                          trackPicspageDialogShow({
                            popTitle: 'card_chip_replace_popover',
                            fromPage: fromPageForTrack
                          })
                          setReplaceTargetLabel({
                            id: tid,
                            name: tag.name,
                            color: tag.color
                          })
                        }
                      : undefined
                  }
                  busy={chipBusyId === tid}
                />
              )
              if (!canEditChips || !isReplacing) {
                return (
                  <React.Fragment key={tag.id || idx}>
                    {ellipsisChip}
                  </React.Fragment>
                )
              }
              return (
                <Popover
                  key={tag.id || idx}
                  trigger="click"
                  placement="bottomLeft"
                  open={true}
                  onOpenChange={(v) => {
                    if (!v) setReplaceTargetLabel(null)
                  }}
                  classNames={{ root: 'pcto-popover-overlay' }}
                  content={
                    <TagPickerPopover
                      tags={teamAllTags}
                      currentTagIds={[tid]}
                      onSelect={(newTag) =>
                        handleChipReplaceCommit({
                          id: String(newTag.id),
                          name: newTag.name,
                          color: newTag.color
                        })
                      }
                      onSearch={() =>
                        trackPicspageDialogClick({
                          popTitle: 'card_chip_replace_popover',
                          clickItem: 'search',
                          fromPage: fromPageForTrack
                        })
                      }
                      emptyHint="当前团队暂无可用标签"
                    />
                  }
                >
                  {ellipsisChip}
                </Popover>
              )
            })}
            {remainingCount > 0 && (
              <span className="photo-tag-more">+{remainingCount}</span>
            )}
          </div>
        )}
      </>
    )
  }, [
    shouldRenderTagUI,
    localLabels,
    isBatchTagMode,
    teamAllTags,
    groupId,
    myUserId,
    photoSeqForOp,
    dispatch,
    setLocalLabels,
    chipBusyId,
    replaceTargetLabel,
    setReplaceTargetLabel,
    handleChipRemove,
    handleChipReplaceCommit,
    fromPageForTrack,
    photo
  ])

  return (
    <div
      className={
        'photo-with-tag' + (isBatchTagMode ? ' photo-with-tag--batch-mode' : '')
      }
    >
      <Photo
        {...props}
        onPreview={interceptedOnPreview}
        batchMode={isBatchTagMode}
        renderInPhoto={shouldRenderTagUI ? renderInPhoto : undefined}
      />
    </div>
  )
}

export default PhotoWithTag
